/**
 * Playwright PDF 印刷模式专用页面。
 *
 * 路由：/print/v5/:id?token=<hmac>
 *
 * Celery 容器内 headless Chromium 访问这条 URL，等 `data-report-ready="1"`
 * 信号后调 page.pdf() 出 9 页 PDF（粗略 3 页 + 详细 6 页）。
 *
 * 关键：
 *  - 本路由**不嵌 PrivateRoute**（App.js 路由表里挂在公共段）。鉴权由
 *    后端 print-data API 的 HMAC token 校验承担。
 *  - id='preview' / 'demo' → 走本地 mock，无需 token（方便本地调样式 ——
 *    `npm start` → /print/v5/preview）。
 *  - 真 id → 从 query 拿 token 调 GET /api/reports/<id>/print-data/?token=xxx。
 *  - 数据 + Recharts SVG 渲染完后，双 rAF + 300ms 延迟设置
 *    `document.documentElement.dataset.reportReady = '1'`，Playwright 用
 *    `page.wait_for_selector('html[data-report-ready="1"]')` 同步。
 *
 * 这一步（M-4）先实现 mock 模式以便本地视觉调样式；下一步（M-5）接真 API。
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReportV5 from '../components/report_v5/ReportV5';
import ReportV5Detailed from '../components/report_v5_detailed/ReportV5Detailed';
import { toReportV5ViewModel } from '../adapters/reportV5Adapter';
import { toReportV5DetailedViewModel } from '../adapters/reportV5DetailedAdapter';
import { mockReportDetailedRawPayload } from '../mocks/v5ReportDetailedMock';
import '../styles/print.css';

// 走原生 fetch 而非 api/client.js: 后者拦截器会注 Authorization、401 跳
// /login、5xx 改写 detail —— 印刷模式只认 HMAC token，这些拦截器全是噪音。
async function fetchPrintData(assessmentId, token, variant) {
  // variant 必须透传给后端: print-data view (backend/apps/reports/views.py)
  // 与 DownloadPngView 对齐, rough 不卡 detailed 门, detailed 才卡.
  // 不传 variant → 新企业用户首次下载粗略时 detailed 仍 lazy → 409 →
  // errorMsg 分支 → reportReady 永不置 → Playwright 30s 超时.
  const url = `/api/reports/${encodeURIComponent(assessmentId)}/print-data/`
    + `?token=${encodeURIComponent(token)}`
    + `&variant=${encodeURIComponent(variant || 'rough')}`;
  const resp = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',  // 不带 cookie，强制走 token 路径
  });
  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body };
}

export default function PrintReportV5Page() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id || 'preview';
  const token = searchParams.get('token') || '';
  // variant: 'rough' (默认, 渲染 ReportV5 粗略 3 页) | 'detailed' (渲染
  // ReportV5Detailed 详细 6 页). 后端 ReportDownloadPngView 走 variant query
  // 区分两个独立 PNG 文件, 避免一份图同时含粗略+详细 9 页。
  const variant = (searchParams.get('variant') || 'rough').toLowerCase();

  const [payload, setPayload] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Step 1: 拉数据
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const isMock = id === 'preview' || id === 'demo';
      if (isMock) {
        // 模拟少量延迟，让 Recharts 拿到稳定 layout
        await new Promise((r) => setTimeout(r, 50));
        if (!cancelled) setPayload(mockReportDetailedRawPayload);
        return;
      }
      if (!token) {
        setErrorMsg('缺少 token 参数。印刷模式 URL 形如 /print/v5/<id>?token=<hmac>。');
        return;
      }
      const { ok, status, body } = await fetchPrintData(id, token, variant);
      if (cancelled) return;
      if (!ok) {
        const detail = body?.detail || `HTTP ${status}`;
        setErrorMsg(`拉 print-data 失败 (${status}): ${detail}`);
        return;
      }
      // print-data 响应形态：{report_version, is_fallback, is_detailed_fallback,
      // user_report_v5}。adapter 接受这种 wrapper 形态（reportV5Adapter.js 注释）。
      setPayload(body);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  // Step 2: 数据渲染 + Recharts SVG 画完后，置 ready 信号
  useEffect(() => {
    if (!payload) return undefined;
    // ResponsiveContainer 用 ResizeObserver，首帧后才画 SVG。
    // 双 rAF + 300ms：第一个 rAF 等 React 渲染，第二个 rAF 等 Recharts ResizeObserver
    // 回调，再 300ms 让动画收敛 / 字体渲染稳定。
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        timer = window.setTimeout(() => {
          document.documentElement.dataset.reportReady = '1';
          window.__reportReady = true;  // 旁路信号，调试用
        }, 300);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (timer) window.clearTimeout(timer);
      delete document.documentElement.dataset.reportReady;
      window.__reportReady = false;
    };
  }, [payload]);

  if (errorMsg) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
        <h2>印刷模式暂未就绪</h2>
        <p>{errorMsg}</p>
      </div>
    );
  }
  if (!payload) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>正在加载报告…</div>
    );
  }

  if (variant === 'detailed') {
    const vmDetailed = toReportV5DetailedViewModel(payload);
    return <ReportV5Detailed vm={vmDetailed} assessmentId={id} printMode />;
  }
  // 默认 / variant=rough
  const vmRough = toReportV5ViewModel(payload);
  return <ReportV5 vm={vmRough} assessmentId={id} printMode />;
}
